import { Grid, Text, UnstyledButton, useMantineTheme } from '@mantine/core';
import {
  IconCalendarEvent,
  IconUserCircle,
  IconShield,
  IconInfoSquare,
  IconCalendarDue,
} from '@tabler/icons-react';
import { Fragment, useMemo, useState } from 'react';
import type { Sport } from '@betterpool/api-types/contests-service';
import { ContestType } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';
import { format } from 'date-fns';

import { Link } from '~/domains/common/components/Link/Link';
import { Match } from '~/domains/common/components/Match/Match';
import dt from '~/testing';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import { isContestPlayerBased } from '~/domains/contest/domains/common/utils/getContestBased';
import type { ContestSlate } from '~/domains/games/interfaces/contest.slates.interface';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';

import {
  Section,
  SectionContent,
  SectionGrid,
  SectionGridCol,
  SectionGridLabel,
  SectionGridValue,
  SectionTitle,
} from '../components';

import ContestIcon from './ContestIcon';
import { getSlatesName, getSportText } from './about.utils';
import GameModal from './Game/GameModal';
import getPickDeadlineValue from './getPickDeadlineValue';

type AboutProps = {
  owner: string;
  rosterRequirements: string;
  ownerHref: string;
  contestType: ContestType;
  slates: ContestSlate[];
  description?: string;
  createdByAdmin?: boolean;
  maxEntriesPerUser?: number;
  isSidePanel?: boolean;
  sport?: Sport;
  pickDeadlineType?: FullContest['settings']['pickDeadlineType'];
  entryDeadline: Date;
};

function About({
  description,
  rosterRequirements,
  owner,
  ownerHref,
  contestType,
  slates = [],
  createdByAdmin,
  maxEntriesPerUser,
  isSidePanel,
  sport,
  pickDeadlineType = 'slate',
  entryDeadline,
}: AboutProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const theme = useMantineTheme();
  const slate = useMemo(() => getSlatesName(slates), [slates]);
  const { t } = useTranslation('contest');
  const isPlayerBased = isContestPlayerBased(contestType);

  const isMultiEntry = maxEntriesPerUser > 1;

  const pickDeadlineValue = getPickDeadlineValue({
    isPlayerBased,
    sport,
    pickDeadlineType,
    slates,
    t,
  });

  const values = useMemo(
    () =>
      [
        {
          'data-test-id': dt.contestDetail.section.about.sport,
          Icon: () => <ContestIcon className="icon" sport={sport} />,
          id: 1,
          label: t('detail.about.info.sport'),
          value: getSportText(sport),
        },
        {
          'data-test-id': dt.contestDetail.section.about.contestType,
          Icon: () => <IconShield className="icon" stroke={2} size={20} />,
          id: 2,
          label: t('detail.about.info.contestType'),
          value: (
            <Match value={contestType}>
              <Match.On value={[ContestType.PLAYER_TIER]}>{t('common:contest.tiers')}</Match.On>
              <Match.On value={[ContestType.TEAM_PICK_EM]}>{t('common:contest.pickEm')}</Match.On>
              <Match.On value={[ContestType.TEAM_SURVIVOR]}>
                {t('common:contest.survivor')}
              </Match.On>
              <Match.On value={[ContestType.PLAYER_ONE_AND_DONE]}>
                {t('common:contest.oneAndDone')}
              </Match.On>
            </Match>
          ),
        },
        {
          'data-test-id': dt.contestDetail.section.about.multiEntry,
          Icon: () => <IconInfoSquare className="icon" stroke={2} size={20} />,
          id: 3,
          label: t('detail.about.info.multiEntry'),
          value: isMultiEntry
            ? t('detail.about.info.yes', { entries: maxEntriesPerUser })
            : t(`detail.about.info.no`),
        },
        {
          'data-test-id': dt.contestDetail.section.about.creator,
          Icon: () => <IconUserCircle className="icon" stroke={2} size={20} />,
          id: 4,
          label: t('detail.about.info.creator'),
          value: owner ? (
            <Link href={ownerHref} passHref>
              {createdByAdmin ? t('detail.about.info.adminCreator') : owner}
            </Link>
          ) : (
            t('detail.about.info.adminCreator')
          ),
        },
        {
          'data-test-id': dt.contestDetail.section.about.slate,
          Icon: () => <IconCalendarEvent className="icon" stroke={2} size={20} />,
          id: 5,
          label: t('detail.about.info.slate'),
          value: slate,
        },
        {
          'data-test-id': dt.contestDetail.section.about.entryDeadline,
          Icon: () => <IconCalendarDue className="icon" stroke={2} size={20} />,
          id: 6,
          label: t('detail.about.info.entryDeadline'),
          value: format(entryDeadline, "ccc M/dd '@' h:mm a"),
        },
        pickDeadlineValue && {
          'data-test-id': dt.contestDetail.section.about.pickDeadline,
          Icon: () => <IconCalendarDue className="icon" stroke={2} size={20} />,
          id: 7,
          label: t('detail.about.info.pickDeadline'),
          value: pickDeadlineValue,
        },
      ].filter(Boolean),
    [
      t,
      sport,
      contestType,
      isMultiEntry,
      maxEntriesPerUser,
      owner,
      ownerHref,
      createdByAdmin,
      slate,
      entryDeadline,
      pickDeadlineValue,
    ]
  );

  return (
    <Section>
      <SectionTitle data-test-id={dt.contestDetail.section.about.title} isSidePanel={isSidePanel}>
        {t('detail.about.title')}
      </SectionTitle>
      <SectionContent isSidePanel={isSidePanel}>
        {description ? (
          <SectionGridLabel
            data-test-id={dt.contestDetail.section.about.description}
            isSidePanel={isSidePanel}
            mb={theme.spacing.lg}
            dangerouslySetInnerHTML={{ __html: toSafeMarkup(description) }}
          />
        ) : null}
        {rosterRequirements ? (
          <SectionGridLabel
            data-test-id={dt.contestDetail.section.about.rosterRequirements}
            isSidePanel={isSidePanel}
            mb={theme.spacing.lg}
          >
            {rosterRequirements}
          </SectionGridLabel>
        ) : null}
        {values.map(({ label, Icon, value, id, ...rest }) => (
          <Fragment key={id}>
            <SectionGrid data-test-id={rest['data-test-id']}>
              <SectionGridCol span={6} xs={5} md={6}>
                <Grid align="center">
                  <Icon />
                  <SectionGridLabel isSidePanel={isSidePanel} ml="xl">
                    {label}
                  </SectionGridLabel>
                </Grid>
              </SectionGridCol>
              <SectionGridCol span={6} xs={7} md={6}>
                <SectionGridValue isSidePanel={isSidePanel}>{value}</SectionGridValue>
              </SectionGridCol>
            </SectionGrid>
          </Fragment>
        ))}
        {slates.length > 0 && (
          <UnstyledButton onClick={() => setIsModalOpen(true)}>
            <Text color="cyan.5" size="sm">
              {t('detail.about.game.action')}
            </Text>
          </UnstyledButton>
        )}
        <GameModal isOpen={isModalOpen} slates={slates} onClose={() => setIsModalOpen(false)} />
      </SectionContent>
    </Section>
  );
}

export default About;

export type { AboutProps };
